import { useState } from "react";
import {
  formatDateOnly,
  formatPublicEventDate,
  isChapterOrganization,
  publicEventTimezone,
  workspaceNoun
} from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";

export function MemberPortalEventsPanel({
  busyEventId = "",
  eventLabel = "Events",
  eventPluralLabel = "events",
  events = [],
  eventRegistrations = [],
  member = null,
  memberEventDisplayDescription,
  memberEventDisplayTitle,
  onRegisterForEvent,
  publicEventAttendanceStatusLabel,
  publicEventAvailabilityLabel,
  publicEventInstructorText,
  publicEventRegistrationStatusLabel,
  range = {},
  registrationLooksLikeOrientation
}) {
  const [eventSection, setEventSection] = useState("schedule");
  const workspace = workspaceNoun(range, "range");
  const eventDetailFallback = isChapterOrganization(range)
    ? "Class details will be confirmed by the chapter team."
    : "Class details will be confirmed by the range team.";
  const visibleEvents = Array.isArray(events) ? events : [];
  const registrationByEventId = new Map(
    (eventRegistrations || [])
      .filter((registration) => registration?.eventId)
      .map((registration) => [registration.eventId, registration])
  );
  const memberState = member
    ? member.status === "active"
      ? "Ready when published"
      : "Available after review"
    : "Membership needed";
  const memberDetail = member
    ? member.status === "active"
      ? `Published ${eventPluralLabel} will show registration actions here.`
      : `Your membership is recorded. Member ${eventPluralLabel} may require active access before registration.`
    : `Start or connect a membership before member-only ${eventPluralLabel} can be shown.`;

  return (
    <section className="member-portal-events-panel" id="events">
      <div className="member-portal-dashboard-header">
        <div>
          <span className="public-section-kicker">{eventLabel}</span>
          <h2>{visibleEvents.length > 0 ? `${eventLabel} open now` : `${eventLabel} are not open yet`}</h2>
          <p>
            {visibleEvents.length > 0
              ? `Review upcoming ${eventPluralLabel}, registration timing, and what to prepare before attending.`
              : `When the ${workspace} opens member ${eventPluralLabel}, this page will show registration options, waitlist state, and attendance updates.`}
          </p>
        </div>
      </div>

      <MemberPortalSectionTabs
        activeSection={eventSection}
        onSelect={setEventSection}
        sections={[
          { id: "schedule", label: "Schedule", detail: "Published events" },
          { id: "registrations", label: "Registrations", detail: "Your status" },
          { id: "prep", label: "Prep", detail: "What to do now" }
        ]}
      />

      {eventSection === "schedule" && (
        <>
          <div className="member-portal-status-grid">
            <div>
              <span>Published now</span>
              <strong>{visibleEvents.length || "None"}</strong>
              <small>{visibleEvents.length ? `${eventLabel} available for this membership.` : `No member ${eventPluralLabel} are open in the portal yet.`}</small>
            </div>
            <div>
              <span>Your access</span>
              <strong>{memberState}</strong>
              <small>{memberDetail}</small>
            </div>
            <div>
              <span>Registrations</span>
              <strong>{eventRegistrations?.length || "None yet"}</strong>
              <small>Your confirmed, waitlisted, and cancelled registrations will appear here.</small>
            </div>
          </div>
          {visibleEvents.length > 0 && (
            <div className="member-portal-split-list member-portal-full-list">
              <div>
                <h3>{eventLabel} schedule</h3>
                {visibleEvents.slice(0, 8).map((event) => {
                  const existingRegistration = registrationByEventId.get(event.eventId);
                  const activeRegistration = existingRegistration && !["cancelled"].includes(existingRegistration.status);
                  const availabilityLabel = publicEventAvailabilityLabel(event);
                  const eventTimezone = publicEventTimezone(event, range);
                  const registrationAvailable = !availabilityLabel;
                  const canRegister = member?.status === "active" && registrationAvailable && !activeRegistration;
                  return (
                    <article key={event.eventId}>
                      <strong>{memberEventDisplayTitle(event)}</strong>
                      <span>{memberEventDisplayDescription(event, eventDetailFallback)}</span>
                      <span>{formatPublicEventDate(event.startsAt, eventTimezone)} / {event.location || "Location announced by the organization"}</span>
                      {event.instructors?.length > 0 && (
                        <span>
                          Instructor: {event.instructors.map(publicEventInstructorText).join(", ")}
                        </span>
                      )}
                      <em>
                        {activeRegistration
                          ? `Registered / ${publicEventRegistrationStatusLabel(existingRegistration.status)}`
                          : availabilityLabel || event.pricing?.memberLabel || event.pricing?.label || "Registration available"}
                      </em>
                      <div className="member-portal-support-actions">
                        {activeRegistration ? (
                          <span className="compact-status-row">Registration is already on your account.</span>
                        ) : (
                          <button
                            type="button"
                            className="secondary-button"
                            disabled={!canRegister || busyEventId === event.eventId}
                            onClick={() => onRegisterForEvent?.(event)}
                          >
                            {busyEventId === event.eventId
                              ? "Registering..."
                              : member?.status === "active"
                                ? "Register"
                                : "Available after membership review"}
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {eventSection === "registrations" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Your registrations</h3>
            {(eventRegistrations || []).length === 0 ? (
              <div className="compact-status-row">No class registrations are tied to this member account yet.</div>
            ) : (eventRegistrations || []).map((registration) => {
              const registrationEvent = registration.event || visibleEvents.find((event) => event.eventId === registration.eventId) || {};
              const registrationTimezone = publicEventTimezone(registrationEvent, range);
              return (
                <article key={registration.registrationId || registration.eventId || registration.email}>
                  <strong>{memberEventDisplayTitle(registrationEvent.title ? registrationEvent : { title: "Registered class" })}</strong>
                  <span>{registrationEvent.startsAt ? formatPublicEventDate(registrationEvent.startsAt, registrationTimezone) : "Date announced by the organization"}</span>
                  <span>{registrationEvent.location || "Location announced by the organization"}</span>
                  {registrationLooksLikeOrientation(registration) && (
                    <span>
                      Orientation attendance: {publicEventAttendanceStatusLabel(registration.attendance?.status)}
                      {registration.attendance?.checkedInAt ? ` / ${formatDateOnly(registration.attendance.checkedInAt)}` : ""}
                    </span>
                  )}
                  {registration.cancellationReason && <span>Reason: {registration.cancellationReason}</span>}
                  <em>{publicEventRegistrationStatusLabel(registration.status)}</em>
                  <div className="member-portal-support-actions">
                    {registration.checkout?.url && (
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => window.location.assign(registration.checkout.url)}
                      >
                        Continue checkout
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {eventSection === "prep" && (
        <div className="member-portal-split-list">
          <div>
            <h3>What to do now</h3>
            <article>
              <strong>Check membership first</strong>
              <span>Some classes may require active membership, orientation, or a qualification before registration opens.</span>
              <em>Membership gates apply</em>
              <div className="member-portal-support-actions">
                <a className="secondary-button" href="#member-status">Open Membership</a>
              </div>
            </article>
            <article>
              <strong>Need timing?</strong>
              <span>Use Support if you need the organization to confirm orientation timing or whether a class will be published soon.</span>
              <em>Ask the organization</em>
              <div className="member-portal-support-actions">
                <a className="secondary-button" href="#member-support">Open Support</a>
              </div>
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
